import { useCallback, useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'

/**
 * useFilledJobIds — Détermine les missions AE/Intérim « pourvues ».
 *
 * Une mission AE ou Intérim est considérée comme « pourvue » dès qu'au moins
 * une candidature associée (même jobId) a le statut `retenu`.
 *
 * Retourne :
 *  - filledJobIds : Set<string> des jobId pourvus (AE/Intérim uniquement)
 *  - loading : booléen de chargement
 *  - isFilled(jobId) : helper pratique
 *  - refetch() : rechargement manuel
 *
 * Le filtrage par type de contrat est appliqué côté requête (AE + Intérim)
 * afin de ne marquer comme « pourvues » que les missions ponctuelles, jamais
 * les CDD/CDI/etc. qui peuvent recevoir plusieurs candidats retenus.
 */
export function useFilledJobIds() {
  const [filledJobIds, setFilledJobIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)

  const fetchFilled = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.applications.list({
        filter: {
          status: 'retenu',
          contractType: { $in: ['auto-entrepreneur', 'interim'] },
        },
        // On ne récupère que les jobId pour limiter le payload.
        projection: { jobId: 1, contractType: 1, status: 1 },
      })

      const ids = new Set<string>()
      ;(list as Array<{ jobId?: string }>).forEach((a) => {
        if (a?.jobId) ids.add(String(a.jobId))
      })
      setFilledJobIds(ids)
    } catch (e) {
      console.error('[useFilledJobIds] fetch failed:', e)
      // En cas d'échec, on garde un Set vide pour ne jamais bloquer l'affichage.
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFilled()
  }, [fetchFilled])

  const isFilled = useCallback(
    (jobId: string | undefined | null) => (jobId ? filledJobIds.has(String(jobId)) : false),
    [filledJobIds]
  )

  return { filledJobIds, loading, isFilled, refetch: fetchFilled }
}
